import React, { useState, useEffect } from 'react';
import { authFetch } from '../authConfig';

const API_BASE = process.env.REACT_APP_API_URL || '';

function Settings({ currentUser, onClose, embedded }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [newUser, setNewUser] = useState({ email: '', displayName: '', role: 'user' });

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const res = await authFetch(`${API_BASE}/api/users`);
            if (!res.ok) throw new Error('Failed to fetch users');
            const data = await res.json();
            setUsers(data);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        try {
            const res = await authFetch(`${API_BASE}/api/users`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newUser)
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Failed to add user');
            }

            setShowAddModal(false);
            setNewUser({ email: '', displayName: '', role: 'user' });
            fetchUsers();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleUpdateUser = async (userId, updates) => {
        try {
            const res = await authFetch(`${API_BASE}/api/users/${userId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updates)
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Failed to update user');
            }

            setEditingUser(null);
            fetchUsers();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleToggleActive = async (user) => {
        await handleUpdateUser(user.id, { isActive: !user.isActive });
    };

    const handleDeleteUser = async (user) => {
        if (user.id === currentUser.id) {
            alert('You cannot delete your own account');
            return;
        }

        if (!window.confirm(`Are you sure you want to delete ${user.email}? This cannot be undone.`)) {
            return;
        }

        try {
            const res = await authFetch(`${API_BASE}/api/users/${user.id}`, {
                method: 'DELETE'
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Failed to delete user');
            }

            fetchUsers();
        } catch (err) {
            alert(err.message);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'Never';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    const content = (
        <>
            <div className="settings-content">
                <div className="settings-actions">
                    <button className="add-user-btn" onClick={() => setShowAddModal(true)}>
                        + New User
                    </button>
                </div>

                {loading && <div className="loading">Loading users...</div>}
                {error && <div className="error">{error}</div>}

                {!loading && !error && (
                    <div className="users-table-container">
                        <table className="users-table">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Display Name</th>
                                    <th>Role</th>
                                    <th>Status</th>
                                    <th>Last Login</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(user => (
                                    <tr key={user.id} className={!user.isActive ? 'inactive' : ''}>
                                        <td>{user.email}</td>
                                        <td>
                                            {editingUser?.id === user.id ? (
                                                <input
                                                    type="text"
                                                    value={editingUser.displayName}
                                                    onChange={(e) => setEditingUser({
                                                        ...editingUser,
                                                        displayName: e.target.value
                                                    })}
                                                />
                                            ) : (
                                                user.displayName
                                            )}
                                        </td>
                                        <td>
                                            {editingUser?.id === user.id ? (
                                                <select
                                                    value={editingUser.role}
                                                    onChange={(e) => setEditingUser({
                                                        ...editingUser,
                                                        role: e.target.value
                                                    })}
                                                >
                                                    <option value="user">User</option>
                                                    <option value="admin">Admin</option>
                                                    <option value="superadmin">Super Admin</option>
                                                </select>
                                            ) : (
                                                <span className={`role-badge ${user.role}`}>{user.role}</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                                                {user.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td>{formatDate(user.lastLogin)}</td>
                                        <td className="actions-cell">
                                            {editingUser?.id === user.id ? (
                                                <>
                                                    <button
                                                        className="save-btn"
                                                        onClick={() => handleUpdateUser(user.id, {
                                                            displayName: editingUser.displayName,
                                                            role: editingUser.role
                                                        })}
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        className="cancel-btn"
                                                        onClick={() => setEditingUser(null)}
                                                    >
                                                        Cancel
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        className="edit-btn"
                                                        onClick={() => setEditingUser({
                                                            id: user.id,
                                                            displayName: user.displayName,
                                                            role: user.role
                                                        })}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className={`toggle-btn ${user.isActive ? 'deactivate' : 'activate'}`}
                                                        onClick={() => handleToggleActive(user)}
                                                        disabled={user.id === currentUser.id}
                                                    >
                                                        {user.isActive ? 'Deactivate' : 'Activate'}
                                                    </button>
                                                    <button
                                                        className="delete-btn"
                                                        onClick={() => handleDeleteUser(user)}
                                                        disabled={user.id === currentUser.id}
                                                    >
                                                        Delete
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Add User Modal */}
            {showAddModal && (
                <div className="add-user-overlay">
                    <div className="add-user-modal">
                        <h3>Add New User</h3>
                        <form onSubmit={handleAddUser}>
                            <div className="form-group">
                                <label>Email (must be @vari.com)</label>
                                <input
                                    type="email"
                                    value={newUser.email}
                                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                                    placeholder="user@vari.com"
                                    pattern=".+@vari\.com"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Display Name</label>
                                <input
                                    type="text"
                                    value={newUser.displayName}
                                    onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })}
                                    placeholder="John Doe"
                                />
                            </div>
                            <div className="form-group">
                                <label>Role</label>
                                <select
                                    value={newUser.role}
                                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                                >
                                    <option value="user">User</option>
                                    <option value="admin">Admin</option>
                                    <option value="superadmin">Super Admin</option>
                                </select>
                            </div>
                            <div className="form-actions">
                                <button type="button" onClick={() => setShowAddModal(false)}>Cancel</button>
                                <button type="submit" className="primary">Add User</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );

    const styles = `
        .settings-content {
            padding: 20px;
            overflow-y: auto;
        }
        .settings-actions {
            margin-bottom: 20px;
        }
        .add-user-btn {
            background: var(--medium-blue-accessible);
            color: white;
            border: none;
            padding: 10px 20px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
        }
        .add-user-btn:hover {
            background: var(--dark-blue);
        }
        .users-table-container {
            overflow-x: auto;
        }
        .users-table {
            width: 100%;
            border-collapse: collapse;
        }
        .users-table th,
        .users-table td {
            padding: 12px 10px;
            text-align: left;
            border-bottom: 1px solid var(--border-light);
            color: var(--text-primary);
        }
        .users-table th:first-child,
        .users-table td:first-child {
            min-width: 180px;
            word-break: break-word;
        }
        .users-table th:nth-child(3),
        .users-table td:nth-child(3),
        .users-table th:nth-child(4),
        .users-table td:nth-child(4) {
            width: 80px;
            white-space: nowrap;
            padding: 12px 8px;
        }
        .users-table th {
            background: var(--table-header-bg);
            font-weight: 600;
        }
        .users-table tr.inactive {
            opacity: 0.6;
        }
        .role-badge {
            display: inline-block;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            text-transform: capitalize;
        }
        .role-badge.admin {
            background: #e3f2fd;
            color: #1565c0;
        }
        .role-badge.user {
            background: var(--bg-surface-tertiary);
            color: var(--text-secondary);
        }
        .role-badge.superadmin {
            background: #f3e5f5;
            color: #7b1fa2;
        }
        .status-badge {
            display: inline-block;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
        }
        .status-badge.active {
            background: #e8f5e9;
            color: #2e7d32;
        }
        .status-badge.inactive {
            background: #ffebee;
            color: #c62828;
        }
        .actions-cell {
            white-space: nowrap;
            min-width: 200px;
        }
        .actions-cell button {
            margin-right: 4px;
            margin-bottom: 2px;
            padding: 6px 10px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 12px;
        }
        .actions-cell button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
        .edit-btn {
            background: var(--bg-surface-tertiary);
            color: var(--text-primary);
        }
        .edit-btn:hover:not(:disabled) {
            background: var(--border-primary);
        }
        .save-btn {
            background: #4caf50;
            color: white;
        }
        .cancel-btn {
            background: #9e9e9e;
            color: white;
        }
        .toggle-btn.deactivate {
            background: #ff9800;
            color: white;
        }
        .toggle-btn.activate {
            background: #4caf50;
            color: white;
        }
        .delete-btn {
            background: #f44336;
            color: white;
        }
        .delete-btn:hover:not(:disabled) {
            background: #d32f2f;
        }
        .add-user-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: var(--overlay-bg);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10002;
        }
        .add-user-modal {
            background: var(--bg-surface);
            border-radius: 8px;
            padding: 24px;
            width: 400px;
            max-width: 90%;
            color: var(--text-primary);
        }
        .add-user-modal h3 {
            margin: 0 0 20px;
        }
        .form-group {
            margin-bottom: 16px;
        }
        .form-group label {
            display: block;
            margin-bottom: 6px;
            font-weight: 500;
        }
        .form-group input,
        .form-group select {
            width: 100%;
            padding: 10px;
            border: 1px solid var(--border-input);
            border-radius: 4px;
            font-size: 14px;
            background: var(--bg-input);
            color: var(--text-primary);
        }
        .form-actions {
            display: flex;
            justify-content: flex-end;
            gap: 10px;
            margin-top: 20px;
        }
        .form-actions button {
            padding: 10px 20px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
        }
        .form-actions button.primary {
            background: var(--medium-blue-accessible);
            color: white;
        }
        .users-table input,
        .users-table select {
            padding: 6px;
            border: 1px solid var(--border-input);
            border-radius: 4px;
            background: var(--bg-input);
            color: var(--text-primary);
        }
    `;

    if (embedded) {
        return <>{content}<style>{styles}</style></>;
    }

    return (
        <div className="settings-overlay">
            <div className="settings-modal">
                <div className="settings-header">
                    <h2>User Management</h2>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>
                {content}
            </div>
            <style>{`
                .settings-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: var(--overlay-bg);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                }
                .settings-modal {
                    background: var(--bg-surface);
                    border-radius: 8px;
                    width: 90%;
                    max-width: 1000px;
                    max-height: 90vh;
                    display: flex;
                    flex-direction: column;
                    box-shadow: var(--shadow-lg);
                }
                .settings-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 20px;
                    border-bottom: 1px solid var(--border-light);
                    color: var(--text-primary);
                }
                .settings-header h2 {
                    margin: 0;
                    font-size: 1.5rem;
                }
                .close-btn {
                    background: none;
                    border: none;
                    font-size: 24px;
                    cursor: pointer;
                    color: var(--text-secondary);
                }
                .close-btn:hover {
                    color: var(--text-primary);
                }
                ${styles}
            `}</style>
        </div>
    );
}

export default Settings;
